/** @jsx isolateComponent */
import { css } from '@emotion/core';

import { createKeyHandler } from '@zapier/product-get-help/src/utils';
import { isolateComponent } from '@zapier/style-encapsulation';
import {
  Animation,
  Colors,
  Icon,
  Shadows,
  Typography,
} from '@zapier/design-system';

type IconName = any; // TODO: figure out https://zapier.slack.com/archives/C0466L7SX/p1584045313008500
type RadioInputProps = {
  /** Optional name of an icon to render before the label */
  icon?: IconName;
  /** Label */
  label: string;
  /** Function to call on click */
  onClick: () => void;
};

const buttonCss = (hasIcon: boolean) => css`
  ${Typography.smallPrint1Semibold}
  transition-property: box-shadow, border-color, background-color, color;
  transition-timing-function: ${Animation.transitionTimingFunction};
  transition-duration: ${Animation.transitionDuration};
  position: relative;
  cursor: pointer;
  height: 50px;
  border-width: 1px;
  border-style: solid;
  border-color: ${Colors.neutral200};
  border-radius: 5px;
  background-color: ${Colors.neutral200};
  padding-left: 13px;
  padding-right: 13px;
  display: grid;
  grid-column-gap: 13px;
  grid-template-columns: ${hasIcon
    ? 'min-content 1fr min-content'
    : '1fr min-content'};
  align-items: center;
  label {
    color: ${Colors.neutral700};
    whitespace: nowrap;
    overflow: hidden;
    textoverflow: ellipsis;
  }
  // This is the outline that renders around the button when
  // it's focused. It's a pseudo element to preserve transparency
  // between the edge of the button and the edge of the outline.
  // It's defined here vs only in ':focus' so that it can transition
  // between states.
  &::before {
    content: '';
    position: absolute;
    pointer-events: none;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    border: 3px solid transparent;
    border-radius: 9px;
    transition-property: all;
    transition-timing-function: ${Animation.transitionTimingFunction};
    transition-duration: ${Animation.transitionDuration};
  }
  &:hover,
  &:focus {
    box-shadow: ${Shadows.elevation10};
    border-radius: 5px;
    border-color: ${Colors.neutral400};
    background-color: ${Colors.neutral100};
    label {
      color: ${Colors.blueJeans};
    }
    span {
      // color of the arrow
      fill: ${Colors.blueJeans};
    }
  }
  &:focus::before {
    border-color: ${Colors.blueJeans};
    top: -6px;
    right: -6px;
    bottom: -6px;
    left: -6px;
  }
  ${hasIcon &&
  css`
    &:hover span:first-of-type,
    &:focus span:first-of-type {
      // color of the icon
      fill: ${Colors.blueJeans};
    }
  `}
`;

const labelCss = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * Renders a single item for the `RadioBlock`.
 */
const RadioInput = ({ icon, label, onClick }: RadioInputProps) => {
  return (
    <div
      css={buttonCss(!!icon)}
      onClick={onClick}
      onKeyDown={createKeyHandler(['enter'], onClick)}
      role="button"
      tabIndex={0}
    >
      {icon && <Icon color={'neutral800'} name={icon} size={20} />}
      <label css={labelCss}>{label}</label>
      <Icon color={'neutral600'} name="arrowSmallRight" size={20} />
    </div>
  );
};

export default RadioInput;
